import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, photoUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User, Newspaper } from "lucide-react";

// Only allow `color` and `background-color` inside `style` attributes,
// and reject anything that could smuggle JS (url(), expression, @import, javascript:).
if (typeof window !== "undefined" && !window.__t2c_purify_hook__) {
  DOMPurify.addHook("uponSanitizeAttribute", (_node, data) => {
    if (data.attrName !== "style") return;
    const allowedProps = new Set(["color", "background-color"]);
    const declarations = String(data.attrValue || "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    const safe = [];
    for (const d of declarations) {
      const idx = d.indexOf(":");
      if (idx < 0) continue;
      const prop = d.slice(0, idx).trim().toLowerCase();
      const val = d.slice(idx + 1).trim();
      if (!allowedProps.has(prop) || !val) continue;
      if (/url\(|expression|javascript:|@import|<|>/i.test(val)) continue;
      safe.push(`${prop}: ${val}`);
    }
    data.attrValue = safe.join("; ");
    if (!data.attrValue) data.keepAttr = false;
  });
  window.__t2c_purify_hook__ = true;
}

const FALLBACK_COVER = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80&auto=format&fit=crop";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-DO", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/news/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-16 text-center text-muted-foreground">Cargando…</div>;

  if (notFound || !article) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-24 text-center" data-testid="news-not-found">
        <Newspaper className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
        <h1 className="font-serif text-4xl">Artículo no encontrado</h1>
        <p className="mt-3 text-muted-foreground">Este artículo pudo haber sido movido o retirado.</p>
        <Button asChild className="mt-6 rounded-full">
          <Link to="/noticias"><ArrowLeft className="h-4 w-4 mr-2" /> Volver a Noticias</Link>
        </Button>
      </div>
    );
  }

  return (
    <article className="mx-auto max-w-3xl px-5 sm:px-6 md:px-4 pt-4 md:pt-12 pb-16" data-testid="news-detail">
      <Link to="/noticias" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-5" data-testid="back-to-news">
        <ArrowLeft className="h-4 w-4" /> Noticias
      </Link>

      <div className="mb-6">
        <div className="label-eyebrow text-secondary" data-testid="article-category">{article.category}</div>
        <h1 className="mt-2 font-serif text-[2rem] sm:text-4xl md:text-5xl leading-[1.1] break-words" data-testid="article-title">{article.title}</h1>
        <div className="mt-4 flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground flex-wrap">
          <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {article.author_name || "Editorial to2casa"}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {formatDate(article.published_at || article.created_at)}</span>
        </div>
      </div>

      <div className="aspect-[16/9] overflow-hidden rounded-sm bg-muted mb-6 md:mb-8 -mx-5 sm:mx-0">
        <img
          src={article.cover_photo ? photoUrl(article.cover_photo) : FALLBACK_COVER}
          alt={article.title}
          className="h-full w-full object-cover"
          data-testid="article-cover"
        />
      </div>

      <div
        className="article-content"
        data-testid="article-content"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(article.content || "", {
            ALLOWED_TAGS: [
              "p", "br", "strong", "em", "u", "s", "b", "i",
              "h2", "h3", "h4",
              "ul", "ol", "li",
              "blockquote", "a", "span",
            ],
            ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
          }),
        }}
      />
    </article>
  );
}
